"use client";

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/app/admin/_common/ui/dialog";
import { Sheet, SheetContent, SheetTitle } from "@/app/admin/_common/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import { Badge } from "@/app/admin/_common/ui/badge";
import CuoButton from "@/app/admin/_common/CuoButton";
import { cn } from "@/app/_utils/cn";
import GuestService from "@/app/_services/GuestService";
import { Upload, Download, FileUp, X } from "lucide-react";
import { toast } from "@/app/_utils/toast";
import { useIsMobile } from "@/app/admin/_hooks/useIsMobile";

type RowResult = {
  row: number;
  status: "created" | "updated" | "error";
  message: string | null;
  first_name: string;
  last_name: string;
};

const RESULT_BADGE_CLASSES: Record<RowResult["status"], string> = {
  created: "border-emerald-200 bg-emerald-50 text-emerald-700",
  updated: "border-blue-200 bg-blue-50 text-blue-700",
  error: "border-red-200 bg-red-50 text-red-600",
};

const RESULT_DOT_CLASSES: Record<RowResult["status"], string> = {
  created: "bg-emerald-500",
  updated: "bg-blue-500",
  error: "bg-red-500",
};

const RESULT_LABELS: Record<RowResult["status"], string> = {
  created: "Created",
  updated: "Updated",
  error: "Failed",
};

export default function WaitlistCsvUploadDialog({
  open,
  onClose,
  getData,
}: {
  open: boolean;
  onClose: () => void;
  getData: () => void | Promise<void>;
}) {
  const [uploadCSV, setUploadCSV] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [invalidFile, setInvalidFile] = useState(false);
  const [lastUpload, setLastUpload] = useState<{
    created: number;
    updated: number;
    failed: number;
    results: RowResult[];
  } | null>(null);

  useEffect(() => {
    if (!open) {
      setLastUpload(null);
      setUploadCSV(null);
      setFileName(null);
      setInvalidFile(false);
    }
  }, [open]);

  async function downloadTemplate() {
    const res = await GuestService.downloadWaitlistBulkTemplate();
    const url = window.URL.createObjectURL(res);
    const link = document.createElement("a");
    link.href = url;
    link.download = "waitlist_guests_template.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) {
      setUploadCSV(null);
      setFileName(null);
      return;
    }
    if (file.size > 15 * 1000 * 1000) {
      setInvalidFile(true);
      toast.warning("File too large (max 15MB)");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadCSV(reader.result as string);
      setFileName(file.name);
      setInvalidFile(false);
    };
    reader.readAsDataURL(file);
  }

  async function handleUpload() {
    if (!uploadCSV) {
      toast.warning("Select a CSV file first");
      return;
    }
    setBusy(true);
    setLastUpload(null);
    try {
      const result = await GuestService.bulkWaitlistUpload(uploadCSV);
      const { created, updated, errors, results } = result;
      const failed = errors?.length ?? 0;
      const rowResults: RowResult[] = Array.isArray(results)
        ? results.map((r: any) => ({
            row: r.row,
            status: r.status,
            message: r.message ?? null,
            first_name: r.first_name ?? "",
            last_name: r.last_name ?? "",
          }))
        : [];
      setLastUpload({ created, updated, failed, results: rowResults });
      const msg = `Created ${created}, updated ${updated}${failed > 0 ? `, ${failed} failed` : ""}. See table below.`;
      if (failed > 0) {
        toast.warning(msg);
      } else {
        toast.success(msg);
      }
      await getData();
      setUploadCSV(null);
      setFileName(null);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Upload failed";
      toast.error(message);
    } finally {
      setBusy(false);
    }
  }

  function handleCloseDialog() {
    if (busy) return;
    setLastUpload(null);
    setUploadCSV(null);
    onClose();
  }

  const isMobile = useIsMobile();

  const body = (
    <>
      <div className="flex flex-col gap-3">
          <CuoButton
            variant="outline"
            label="Download CSV template"
            icon={<Download className="size-3.5" />}
            onClick={downloadTemplate}
            className="w-full"
          />

          <input
            type="file"
            accept=".csv"
            disabled={busy}
            onChange={handleFileChange}
            className={cn(
              "w-full cursor-pointer rounded-md border border-dashed bg-muted/40 p-4 text-[12.5px] text-foreground",
              invalidFile && "border-red-600 text-red-600",
              busy && "cursor-default opacity-60"
            )}
          />

          {lastUpload && lastUpload.results.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="text-[13px] font-semibold text-foreground">
                Import results (CSV row = spreadsheet row number)
              </div>
              <div className="text-[12.5px] text-muted-foreground">
                Created {lastUpload.created}, updated {lastUpload.updated}
                {lastUpload.failed > 0 ? `, failed ${lastUpload.failed}` : ""}
              </div>
              <div className="max-h-[360px] overflow-y-auto rounded-md border border-border">
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow className="sticky top-0 z-10 hover:bg-muted [&>th]:bg-muted">
                      <TableHead className="h-8 w-[10%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Row
                      </TableHead>
                      <TableHead className="h-8 w-[18%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        First name
                      </TableHead>
                      <TableHead className="h-8 w-[18%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Last name
                      </TableHead>
                      <TableHead className="h-8 w-[16%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Result
                      </TableHead>
                      <TableHead className="h-8 w-[38%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Details
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lastUpload.results.map((r) => (
                      <TableRow key={r.row}>
                        <TableCell className="text-[12.5px] text-muted-foreground">{r.row}</TableCell>
                        <TableCell className="truncate text-[12.5px] text-foreground">
                          {r.first_name || "—"}
                        </TableCell>
                        <TableCell className="truncate text-[12.5px] text-foreground">
                          {r.last_name || "—"}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`gap-1.5 rounded-[7px] font-normal ${RESULT_BADGE_CLASSES[r.status]}`}
                          >
                            <span className={`size-1.5 flex-none rounded-full ${RESULT_DOT_CLASSES[r.status]}`} />
                            {RESULT_LABELS[r.status]}
                          </Badge>
                        </TableCell>
                        <TableCell
                          className={cn(
                            "whitespace-normal break-words text-[12.5px]",
                            r.status === "error" ? "text-red-600" : "text-muted-foreground"
                          )}
                          title={r.message ?? undefined}
                        >
                          {r.status === "error" ? r.message ?? "—" : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
    </>
  );

  const footerButtons = (
    <>
      <CuoButton
        variant="outline"
        label={lastUpload ? "Close" : "Cancel"}
        disabled={busy}
        onClick={handleCloseDialog}
      />
      {!lastUpload && (
        <CuoButton
          variant="default"
          label="Upload"
          icon={<Upload className="size-3.5" />}
          disabled={!uploadCSV}
          disableOnAsync={busy}
          onClick={handleUpload}
        />
      )}
      {lastUpload && (
        <CuoButton
          variant="default"
          label="Import another file"
          disabled={busy}
          onClick={() => setLastUpload(null)}
        />
      )}
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(next) => !next && handleCloseDialog()}>
        <SheetContent side="bottom" hideClose className="flex max-h-[85vh] flex-col gap-0 overflow-hidden rounded-t-xl bg-white p-0">
          <SheetTitle className="sr-only">Import waitlist guests</SheetTitle>
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <span className="inline-flex items-center gap-2 text-[13px] font-normal text-foreground">
              <FileUp className="size-4 flex-none text-muted-foreground" />
              Import waitlist guests
            </span>
            <button
              type="button"
              onClick={handleCloseDialog}
              disabled={busy}
              className="flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
              aria-label="Close"
            >
              <X className="size-3.5" />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
            <p className="text-[12.5px] text-muted-foreground">
              Each row needs first_name plus email or phone, and is treated as a new signup at the
              front of the queue. Existing guests are matched by email, then phone.
            </p>
            {body}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-border p-3">
            {footerButtons}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleCloseDialog()}>
      <DialogContent
        className={cn(
          "inset-0 left-0 top-0 m-auto h-fit max-h-[85vh] w-[calc(100%-2rem)] translate-x-0 translate-y-0 bg-white",
          lastUpload ? "max-w-3xl" : "max-w-[480px]"
        )}
      >
        <DialogHeader className="gap-2">
          <DialogTitle>
            <span className="inline-flex items-center gap-2 align-middle">
              <FileUp className="size-4 flex-none text-muted-foreground" />
              Import waitlist guests
            </span>
          </DialogTitle>
          <DialogDescription>
            Each row needs first_name plus email or phone, and is treated as a new signup at the
            front of the queue. Existing guests are matched by email, then phone.
          </DialogDescription>
        </DialogHeader>

        {body}

        <DialogFooter className="flex-row justify-end gap-2 space-x-0">{footerButtons}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
